import { css } from '@emotion/react'
import { usePecField } from 'components/form'
import { PrivateRoute } from 'components/route'
import { useAtendimentoContext } from 'hooks/atendimento-context/useAtendimentoContext'
import { Fragment, useRef, useState } from 'react'
import { useHistory, useRouteMatch } from 'react-router'
import { NIVEL_SUPERIOR } from 'types/NivelCbo'
import { MetaPath } from 'util/metaPath'

import { CONSULTA_SEGUIMENTO_URL_MODAL } from '../../consulta-seguimento/model-consultaSeguimento'
import { ConsultaSeguimentoEditWarningModal } from '../components/ConsultaSeguimentoEditWarningModal'
import { MChatModalGuard } from '../components/MChatModalGuard'
import { MChatModal } from '../mchat-modal/MChatModal'
import { MCHAT_URL_MODAL, MChatFieldModel, MChatViewModel } from '../model-mchat'
import { MChatFieldAccordionSection } from './MChatFieldAccordionSection'

interface MChatFieldProps {
  name: MetaPath<MChatFieldModel>
  hasConsultaSeguimentoAtual: boolean
  hasConsultaSeguimentoAnterior: boolean
  valuesAtendimentoAnterior: MChatViewModel | null
  isConsultaSeguimentoDesatualizada: boolean
}

export function MChatField(props: Readonly<MChatFieldProps>) {
  const {
    name,
    hasConsultaSeguimentoAtual,
    hasConsultaSeguimentoAnterior,
    valuesAtendimentoAnterior,
    isConsultaSeguimentoDesatualizada,
  } = props

  const ref = useRef<HTMLDivElement>(null)
  const match = useRouteMatch()
  const history = useHistory()

  const modalMChatUrl = `${match.url}${MCHAT_URL_MODAL}`
  const modalConsultaSeguimentoUrl = `${match.url}${CONSULTA_SEGUIMENTO_URL_MODAL}`

  const [showConsultaSeguimentoEditWarningModal, setShowConsultaSeguimentoEditWarningModal] = useState(false)

  const {
    atendimentoProfissional: { id: atendProfId } = {},
    cidadao: { id: cidadaoId },
  } = useAtendimentoContext()

  const {
    input: { onChange },
  } = usePecField({ name })

  const handleSave = (values: MChatFieldModel) => {
    onChange(values)
  }

  const handleDelete = () => {
    onChange(null)
  }

  const handleClose = () => {
    history.push(match.url.split(MCHAT_URL_MODAL)[0])

    if (isConsultaSeguimentoDesatualizada) {
      setShowConsultaSeguimentoEditWarningModal(true)
      return
    }

    setTimeout(() => ref?.current?.focus())
  }

  return (
    <Fragment>
      <div ref={ref} tabIndex={-1} css={styles.root} data-testid='MChatField'>
        <MChatFieldAccordionSection
          name={name}
          hasConsultaSeguimento={hasConsultaSeguimentoAtual || hasConsultaSeguimentoAnterior}
          valuesAtendimentoAnterior={valuesAtendimentoAnterior}
          modalUrl={modalMChatUrl}
          onDelete={handleDelete}
        />
      </div>

      <PrivateRoute cbos={NIVEL_SUPERIOR} path={modalMChatUrl} exact>
        <MChatModalGuard hasConsultaSeguimentoAtual={hasConsultaSeguimentoAtual} onClose={handleClose}>
          <MChatModal
            name={name}
            atendProfId={atendProfId}
            cidadaoId={cidadaoId}
            isEditing={false}
            isConsultaSeguimentoDesatualizada={isConsultaSeguimentoDesatualizada}
            modalConsultaSeguimentoUrl={modalConsultaSeguimentoUrl}
            onSave={handleSave}
            onClose={handleClose}
          />
        </MChatModalGuard>
      </PrivateRoute>

      <PrivateRoute cbos={NIVEL_SUPERIOR} path={`${modalMChatUrl}/editar`} exact>
        <MChatModalGuard hasConsultaSeguimentoAtual={hasConsultaSeguimentoAtual} onClose={handleClose}>
          <MChatModal
            name={name}
            atendProfId={atendProfId}
            cidadaoId={cidadaoId}
            isEditing={true}
            isConsultaSeguimentoDesatualizada={isConsultaSeguimentoDesatualizada}
            modalConsultaSeguimentoUrl={modalConsultaSeguimentoUrl}
            onSave={handleSave}
            onClose={handleClose}
          />
        </MChatModalGuard>
      </PrivateRoute>

      <ConsultaSeguimentoEditWarningModal
        open={showConsultaSeguimentoEditWarningModal}
        onClose={() => setShowConsultaSeguimentoEditWarningModal(false)}
      />
    </Fragment>
  )
}

const styles = {
  root: css`
    outline: none;
  `,
}
